import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Container from "@mui/material/Container"
import Typography from "@mui/material/Typography"
import React, { useEffect, useState } from "react"
import { SEO } from "../../components/SEO"
import { PATHS } from "../../constants/paths"
import { Link, NavLink } from "react-router-dom"
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined"
import FormControlLabel from "@mui/material/FormControlLabel"
import Checkbox from "@mui/material/Checkbox"
import CheckBoxOutlineBlankOutlinedIcon from "@mui/icons-material/CheckBoxOutlineBlankOutlined"
import CheckBoxIcon from "@mui/icons-material/CheckBox"
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined"
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined"
import { Pagination } from "./components/Pagination"

import { getTournamentsHistory } from "../../api/tournaments"
import { useAppSelector } from "../../store"

const tabs = ["All", "Daily", "Weekly", "Monthly"]

const getTypeSx = (type) => {
  if (type === "Daily") {
    return {
      color: "#41b7ff",
      borderColor: "rgba(65,183,255,0.22)",
      backgroundColor: "rgba(0,124,255,0.14)",
    }
  }

  if (type === "Monthly") {
    return {
      color: "#c49654",
      borderColor: "rgba(240,175,67,0.22)",
      backgroundColor: "rgba(240,175,67,0.12)",
    }
  }

  return {
    color: "#bca2ff",
    borderColor: "rgba(188,162,255,0.22)",
    backgroundColor: "rgba(188,162,255,0.12)",
  }
}

export const TournamentsHistoryPage = () => {
  const { user } = useAppSelector((state) => state.user)

  const [rows, setRows] = useState([])

  const [activeTab, setActiveTab] = useState("All")
  const [withParticipated, setWithParticipated] = useState(true)

  const [isLoading, setIsLoading] = useState(true)

  const [perPage, setPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const startIndex = (currentPage - 1) * perPage

  // фильтруем турниры, показывая/скрывая те, в которых игрок участвовал
  const participatedRows = withParticipated
    ? rows
    : rows.filter((row) => row.reward === "—")

  // фильтруем турниры по типу (для вкладок)
  const filteredRows =
    activeTab === "All"
      ? participatedRows
      : participatedRows.filter((row) => row.type === activeTab)

  // фильтруем турниры для постраничной навигации
  const visibleRows = filteredRows.slice(startIndex, startIndex + perPage)

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setCurrentPage(1)
  }

  useEffect(() => {
    loadTournament()
  }, [user])

  const loadTournament = async () => {
    setIsLoading(true)

    const { success, tournaments } = await getTournamentsHistory(user?.address)

    if (success) {
      setRows(tournaments)
    }

    setIsLoading(false)
  }

  return (
    <Box>
      <SEO
        title="Zombie TD | Tournaments history"
        description=""
        ogTitle="Zombie TD | Tournaments history"
        ogDescription=""
        ogUrl={PATHS.TOURNAMENTS_HISTORY.PATH}
      />
      <Box className="content">
        <Box className="content-head" mb={4} mt={2}>
          <Container fixed>
            {/* Back button */}
            <Button
              component={Link}
              to={PATHS.TOURNAMENTS.PATH}
              startIcon={<ArrowBackIosNewOutlinedIcon />}
              sx={{
                px: 0,
                color: "#8f877b",
                fontWeight: 900,
                "& .MuiButton-startIcon": {
                  mr: 0.5,
                },
                "& svg": {
                  fontSize: "12px !important",
                },
                "&:hover": {
                  color: "#d69a49",
                  background: "transparent",
                },
              }}
            >
              BACK TO TOURNAMENTS
            </Button>

            {/* Header */}
            <Box>
              <Typography variant="h1">Tournaments history</Typography>

              <Typography
                variant="subtitle1"
                textAlign="left"
                sx={{
                  mb: 2,
                  fontSize: 16,
                  color: "#dedede",
                  maxWidth: {
                    xs: "100%",
                    md: "60%",
                  },
                }}
              >
                View and analyze your past tournament results and performance.
              </Typography>
            </Box>

            {/* Tabs */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 2.4,
                overflowX: "auto",
                borderBottom: "1px solid #121212",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  minWidth: "max-content",
                }}
              >
                {tabs.map((tab) => {
                  const isActive = activeTab === tab

                  return (
                    <Button
                      key={tab}
                      onClick={() => handleTabChange(tab)}
                      sx={{
                        px: 2.4,
                        py: 1.15,
                        color: isActive ? "#c49654" : "#9c9284",
                        fontWeight: 900,
                        borderRadius: "4px 4px 0 0",
                        border: isActive
                          ? "1px solid rgba(196,150,84,0.1)"
                          : "1px solid transparent",
                        background: isActive
                          ? "rgba(196,150,84,0.06)"
                          : "transparent",
                        "&:hover": {
                          borderColor: "rgba(198,146,76,0.45)",
                          background: "rgba(198,146,76,0.08)",
                        },
                      }}
                    >
                      {tab}
                    </Button>
                  )
                })}
              </Box>

              <FormControlLabel
                sx={{
                  mr: 0,
                  color: "#d8d8d8",
                  whiteSpace: "nowrap",
                  ".MuiFormControlLabel-label": {
                    fontSize: 15,
                  },
                }}
                control={
                  <Checkbox
                    checked={withParticipated}
                    onChange={(event) =>
                      setWithParticipated(event.target.checked)
                    }
                    icon={<CheckBoxOutlineBlankOutlinedIcon />}
                    checkedIcon={<CheckBoxIcon />}
                    sx={{
                      color: "#c49654",
                      "&.Mui-checked": {
                        color: "#d69a35",
                      },
                    }}
                  />
                }
                label="Show tournaments I participated in"
              />
            </Box>

            {/* Tournaments list */}
            <Box
              sx={{
                border: "1px solid rgba(196,150,84,0.22)",
                borderRadius: "4px",
                p: { xs: 0, md: 2 },
                backgroundColor: "rgba(0,0,0,0.62)",
                overflowX: "auto",
              }}
            >
              <Box
                sx={{
                  minWidth: {
                    xs: 800,
                    md: 1120,
                  },
                }}
              >
                {/* Table head */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "1.55fr 0.8fr 1.55fr 1fr 0.95fr 0.8fr 0.9fr 1fr 0.9fr",
                    alignItems: "center",
                    px: 1,
                    py: 1.3,
                    color: "#8f96a3",
                    fontSize: 13,
                    fontWeight: 900,
                    textTransform: "uppercase",
                    borderBottom: "1px solid rgba(255,255,255,0.09)",
                  }}
                >
                  <Box>Tournament</Box>
                  <Box>Type</Box>
                  <Box>Date</Box>
                  <Box>Prize Pool</Box>
                  <Box>Entry Fee</Box>
                  <Box>Players</Box>
                  <Box>Your Place</Box>
                  <Box>Your Reward</Box>
                  <Box>Status</Box>
                </Box>

                {/* Table body */}
                {visibleRows.map((row) => (
                  <Box
                    component={NavLink}
                    to={`${PATHS.TOURNAMENT_DETAILS.PATH}${row.id}`}
                    key={`${row.tournament}-${row.date}`}
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "1.55fr 0.8fr 1.55fr 1fr 0.95fr 0.8fr 0.9fr 1fr 0.9fr",
                      alignItems: "center",
                      px: 1,
                      minHeight: 56,
                      color: "#dedede",
                      fontSize: 16,
                      borderBottom: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
                      {row.title}
                    </Typography>

                    <Box>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          px: 1,
                          py: 0.35,
                          border: "1px solid",
                          borderRadius: "4px",
                          fontSize: 12,
                          fontWeight: 900,
                          ...getTypeSx(row.type),
                        }}
                      >
                        {row.type.toUpperCase()}
                      </Box>
                    </Box>

                    <Typography sx={{ color: "#999", fontSize: 16 }}>
                      {row.date}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "#c49654",
                        fontWeight: 900,
                      }}
                    >
                      <EmojiEventsOutlinedIcon sx={{ fontSize: 22 }} />
                      {row.prize_pool} USDT
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "#c49654",
                        fontWeight: 900,
                      }}
                    >
                      {row.entry_fee > 0 ? row.entry_fee + " USDT" : "Free"}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "#f3f6fb",
                        fontWeight: 900,
                      }}
                    >
                      <GroupsOutlinedIcon
                        sx={{ fontSize: 23, color: "#aeb0b6" }}
                      />
                      {row.players}
                    </Box>

                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 900,
                        color: row.place === "—" ? "#c2c2c2" : "#f5f7fb",
                      }}
                    >
                      {row.place}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: row.reward === "—" ? "#1cc371" : "#35c66b",
                        fontWeight: 900,
                      }}
                    >
                      {row.reward}
                    </Box>

                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 96,
                        px: 1.2,
                        py: 0.55,
                        color: "#18e776",
                        border: "1px solid rgba(24,231,118,0.42)",
                        borderRadius: "4px",
                        backgroundColor: "rgba(24,231,118,0.08)",
                        fontSize: 12,
                        fontWeight: 900,
                      }}
                    >
                      FINISHED
                    </Box>
                  </Box>
                ))}

                {/* Table footer */}
                <Pagination
                  currentPage={currentPage}
                  totalItems={filteredRows.length}
                  perPage={perPage}
                  onPageChange={setCurrentPage}
                  onPerPageChange={setPerPage}
                />
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </Box>
  )
}
